// Shared util file
// Contains utility functions for the application
// Create new files for different types of utility functions
// Right now it's generic

import { UrlMatcher, type UrlProps } from 'interweave-autolink'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { isString } from 'lodash'
import { Suspense } from 'react'
import { Link, Route } from 'router-v6'
import { AccessDenied } from 'v2/pages/misc/access-denied'
import { LoadingFallback } from 'v2/shared/utils/lazy-utils'
import type { IRoute, ParentRoute } from 'v2/types'
import type { RUserSession, UserRole } from 'v2/types/api/auth'
import { logger } from './logger'

// Module-level cache for stable role reference
let cachedRoles: UserRole[] = []
let cachedAuthString: string | null = null

export function isParentRoute(route: IRoute): route is ParentRoute {
  return (route as ParentRoute).children !== undefined
}

/** A flattened route plus whether RBAC closed it to this user. */
type ResolvedRoute = IRoute & { accessDenied?: boolean }

const isRouteDenied = (route: IRoute) => Boolean(route.notAllowed) || route.hasAccess === false

/**
 * Flattens nested routes with absolute paths to avoid routing conflicts.
 *
 * A route the user's RBAC closes is kept rather than dropped, and marked
 * `accessDenied` so `generateRoutes` can render the access-denied state. It
 * used to be skipped, which left React Router to fall through to the `*`
 * catch-all and tell the user the page did not exist (Pylon #7741, SER-6206).
 * Denial is inherited: a closed parent closes its children too.
 */
const flattenRoutes = (routes: IRoute[], parentDenied = false): ResolvedRoute[] => {
  const flattened: ResolvedRoute[] = []

  for (const route of routes) {
    if ('sidebarOnly' in route && route.sidebarOnly) continue
    const accessDenied = parentDenied || isRouteDenied(route)

    // Add the current route if it has a component (ChildRoute)
    if ('component' in route && route.component) {
      flattened.push(accessDenied ? { ...route, accessDenied } : route)
    }

    // If it has children, recursively flatten them
    if (route.children) {
      flattened.push(...flattenRoutes(route.children, accessDenied))
    }
  }

  return flattened
}

/**
 * Generates the routes for the application.
 * @param routerArr - The array of routes to generate.
 * @param url - The base URL for the routes.
 * @returns An array of React Router routes.
 */
export const generateRoutes = (routerArr: IRoute[], url = '') => {
  // First, flatten all routes to handle nested structures with absolute paths
  const flattenedRoutes = flattenRoutes(routerArr)

  return flattenedRoutes.map((route) => {
    const routePath = route.path.startsWith('/') ? route.path : `${url}${route.path}`
    const component = 'component' in route ? route.component : null
    // Closed by RBAC: keep the URL working and say so, rather than mounting the
    // page's chunk (and firing its queries) for a user who may not see it.
    const element = route.accessDenied ? (
      <AccessDenied reason={route.accessDenialReason} />
    ) : (
      <Suspense fallback={<LoadingFallback />}>{component}</Suspense>
    )
    return <Route key={routePath} path={routePath} element={element} />
  })
}

// Custom UrlMatcher that uses Link component
export class CustomUrlMatcher extends UrlMatcher {
  replaceWith(children: React.ReactNode, props: UrlProps) {
    return (
      <Link
        to={props.url}
        target="_blank"
        className="underline"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </Link>
    )
  }
}
export const getLocalAuth = (): RUserSession => {
  let auth = {}
  try {
    auth = JSON.parse(localStorage.getItem('auth') || '{}')
  } catch (error) {
    logger.error('Error getting auth', error)
  }
  return auth as RUserSession
}

/**
 * Get all roles for the current session user
 * Returns cached array for stable reference - only creates new array when auth changes
 */
export const getSessionRoles = (): UserRole[] => {
  const authString = localStorage.getItem('auth')
  if (authString === cachedAuthString && cachedRoles) {
    return cachedRoles
  }
  cachedAuthString = authString
  const auth = authString ? JSON.parse(authString) : null
  cachedRoles = auth?.roles || []
  return cachedRoles
}

/**
 * Check if the current session user has a specific role
 */
export const sessionHasRole = (roleName: UserRole): boolean => {
  return getSessionRoles().includes(roleName)
}

export const internationalFormat = (phone = '') => {
  if (!phone) return ''
  const phoneWithPlus = phone.startsWith('+') ? phone : `+${phone}`
  return parsePhoneNumberFromString(phoneWithPlus, 'US')?.formatInternational() || phone
}

export const isValidUrl = (url: string) => {
  const urlPattern = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/
  return isString(url) && urlPattern.test(url) // Ensure input is a string
}

interface MicrophoneMonitorConfig {
  onPermissionChange?: (isDenied: boolean) => void
  cleanup?: () => void
}

export function setupMicrophonePermissionMonitor({
  onPermissionChange,
  cleanup,
}: MicrophoneMonitorConfig = {}) {
  const handlePermissionChange = (permissionState: PermissionState) => {
    const isDenied = permissionState === 'denied'
    onPermissionChange?.(isDenied)
  }

  const setup = async () => {
    try {
      const permission = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      })

      // Initial check
      handlePermissionChange(permission.state)

      // Setup change listener
      const changeListener = () => handlePermissionChange(permission.state)
      permission.addEventListener('change', changeListener)

      // Return cleanup function
      return () => {
        permission.removeEventListener('change', changeListener)
        cleanup?.()
      }
    } catch (error) {
      logger.error('Error checking microphone permission:', error)
      return () => cleanup?.()
    }
  }

  return setup()
}
